import React from 'react';
import { Link } from 'react-router-dom';
import { X, MapPin, Building2, Tag, Globe, Phone, Mail, Star, Award, Heart, Share2, Navigation, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function BoothInfoPanel({ booth, onClose, onNavigate }) {
  const { t } = useTranslation();
  if (!booth) return null;

  const statusColors = {
    occupied: 'bg-emerald-500',
    available: 'bg-slate-400',
    reserved: 'bg-amber-500',
  };

  return (
    <>
      {/* Mobile scrim — dims the map behind the drawer */}
      <div
        className="fixed inset-0 z-[55] bg-slate-950/40 backdrop-blur-sm md:hidden"
        onClick={onClose}
        data-testid="booth-info-scrim"
      />
      <div
        className="
          fixed z-[60] bg-slate-900/95 backdrop-blur-xl shadow-2xl border border-slate-700/50 overflow-hidden flex flex-col
          inset-x-0 bottom-0 top-24 rounded-t-3xl animate-in slide-in-from-bottom duration-300
          md:inset-auto md:right-6 md:top-6 md:bottom-6 md:w-96 md:rounded-3xl md:animate-in md:slide-in-from-right md:duration-300
        "
        data-testid="booth-info-panel"
      >
      {/* Mobile grab handle */}
      <div className="md:hidden pt-2 pb-1 flex justify-center flex-none">
        <div className="w-10 h-1 rounded-full bg-slate-600" />
      </div>
      {/* Header with gradient */}
      <div 
        className="relative p-6 pb-8"
        style={{
          background: `linear-gradient(135deg, ${booth.color || '#3498db'} 0%, ${booth.color || '#3498db'}88 100%)`,
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
          data-testid="close-info-panel-btn"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${statusColors[booth.status]}`}>
            {booth.status?.toUpperCase()}
          </span>
          {booth.is_premium && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-500 text-black">
              {t('booth.premium').toUpperCase()}
            </span>
          )}
          {booth.is_corner && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-500 text-white">
              CORNER
            </span>
          )}
        </div>

        <h2 className="text-3xl font-black text-white mb-1" data-testid="booth-number-title">
          {booth.booth_number}
        </h2>
        <p className="text-white/80 text-sm">
          {booth.hall?.name || 'Outdoor Zone'}
        </p>
      </div>

      {/* Content */}
      <div className="p-6 overflow-y-auto flex-1 scrollbar-thin" data-testid="booth-info-content">
        {booth.company ? (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-black text-xl">
                  {booth.company.name?.[0] || 'C'}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white" data-testid="company-name">
                    {booth.company.name}
                  </h3>
                  <p className="text-slate-400 text-sm">{booth.company.country}</p>
                </div>
              </div>

              <p className="text-slate-300 text-sm leading-relaxed" data-testid="company-description">
                {booth.company.description}
              </p>
            </div>

            {booth.category && (
              <div className="mb-4 flex items-center gap-2">
                <Tag className="w-4 h-4 text-slate-400" />
                <span 
                  className="px-3 py-1 rounded-full text-xs font-semibold text-white"
                  style={{ backgroundColor: booth.category.color }}
                >
                  {booth.category.name}
                </span>
              </div>
            )}

            {/* Company details grid */}
            <div className="grid grid-cols-1 gap-3 mb-6">
              {booth.company.email && (
                <a
                  href={`mailto:${booth.company.email}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-colors border border-slate-700/50"
                  data-testid="company-email-link"
                >
                  <Mail className="w-4 h-4 text-blue-400" />
                  <span className="text-slate-300 text-sm truncate">{booth.company.email}</span>
                </a>
              )}

              {booth.company.phone && (
                <a
                  href={`tel:${booth.company.phone}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-colors border border-slate-700/50"
                  data-testid="company-phone-link"
                >
                  <Phone className="w-4 h-4 text-emerald-400" />
                  <span className="text-slate-300 text-sm">{booth.company.phone}</span>
                </a>
              )}

              {booth.company.website && (
                <a
                  href={booth.company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-colors border border-slate-700/50"
                  data-testid="company-website-link"
                >
                  <Globe className="w-4 h-4 text-purple-400" />
                  <span className="text-slate-300 text-sm truncate">{booth.company.website}</span>
                </a>
              )}
            </div>

            {/* Location info */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-red-400" />
                <span className="text-white font-semibold text-sm">{t('booth.location')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-slate-400">Position X:</span>
                  <span className="text-white ml-2">{booth.position_x}</span>
                </div>
                <div>
                  <span className="text-slate-400">Position Z:</span>
                  <span className="text-white ml-2">{booth.position_z}</span>
                </div>
                <div>
                  <span className="text-slate-400">{t('booth.size')}:</span>
                  <span className="text-white ml-2">{booth.width}x{booth.depth}m</span>
                </div>
                <div>
                  <span className="text-slate-400">Views:</span>
                  <span className="text-white ml-2">{booth.view_count || 0}</span>
                </div>
              </div>
            </div>

            {/* Live flash sales */}
            {Array.isArray(booth.flash_sales) && booth.flash_sales.length > 0 && (
              <div className="mb-6" data-testid="booth-flash-sales">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-red-400 text-lg">⚡</span>
                  <span className="text-white font-semibold text-sm">Live flash sales</span>
                </div>
                <div className="space-y-2">
                  {booth.flash_sales.slice(0, 3).map((fs) => (
                    <Link
                      key={fs.id}
                      to={fs.coupon_code ? `/redeem/${fs.coupon_code}` : `/booth/${booth.id}`}
                      className="flex gap-3 p-2 rounded-xl bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 hover:from-red-500/20 hover:to-orange-500/20 transition-colors"
                      data-testid={`booth-flash-sale-${fs.id}`}
                    >
                      {fs.image_url ? (
                        <img src={fs.image_url} alt={fs.title} className="w-16 h-12 rounded-lg object-cover border border-slate-700 flex-none" />
                      ) : (
                        <div className="w-16 h-12 rounded-lg bg-red-500/20 flex items-center justify-center text-2xl flex-none">⚡</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-bold text-xs truncate">{fs.title}</div>
                        {fs.discount_label && <div className="inline-block bg-yellow-400 text-slate-900 text-[10px] font-black px-1.5 py-0.5 rounded mt-0.5">{fs.discount_label}</div>}
                        {fs.coupon_code && <div className="text-emerald-300 text-[10px] font-mono">Code: {fs.coupon_code}</div>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Products / services */}
            {Array.isArray(booth.products) && booth.products.length > 0 && (
              <div className="mb-6" data-testid="booth-products">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-cyan-400 text-lg">📦</span>
                  <span className="text-white font-semibold text-sm">Products & services</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {booth.products.slice(0, 6).map((p) => (
                    <div key={p.id} className="p-2 rounded-xl bg-slate-800/50 border border-slate-700/50" data-testid={`booth-product-${p.id}`}>
                      {p.image ? (
                        <img src={p.image} alt={p.title} className="w-full h-16 rounded-lg object-cover mb-1.5" />
                      ) : (
                        <div className="w-full h-16 rounded-lg bg-slate-700/40 flex items-center justify-center text-2xl mb-1.5">📦</div>
                      )}
                      <div className="text-white font-bold text-[11px] truncate" title={p.title}>{p.title || p.name}</div>
                      {(p.price_label || p.price) && (
                        <div className="text-cyan-300 text-[10px] font-bold">{p.price_label || `RM ${p.price}`}</div>
                      )}
                      {p.whatsapp && (
                        <a href={`https://wa.me/${p.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="mt-1 inline-block text-[10px] text-green-300 hover:underline">💬 WhatsApp</a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">{t('booth.no_company')}</h3>
            <p className="text-slate-400 text-sm mb-4">
              {t('booth.no_company')}
            </p>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <p className="text-slate-300 text-sm">Rate: RM {booth.price?.toLocaleString() || '5,000'} / event</p>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <Link 
            to={`/booth/${booth.id}`}
            className="col-span-2 flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold text-sm transition-all shadow-lg shadow-emerald-500/30"
            data-testid="view-details-btn"
          >
            <ExternalLink className="w-4 h-4" /> {t('booth.info')}
          </Link>
          <button 
            onClick={() => onNavigate && onNavigate(booth)}
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold text-sm transition-all shadow-lg shadow-blue-500/30"
            data-testid="navigate-btn"
          >
            <Navigation className="w-4 h-4" />
            {t('booth.take_me_there')}
          </button>
          <button 
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm transition-all border border-slate-700/50"
            data-testid="bookmark-btn"
          >
            <Heart className="w-4 h-4" />
            {t('booth.bookmark')}
          </button>
          <button 
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm transition-all border border-slate-700/50"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4" />
            {t('booth.share')}
          </button>
          <button 
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm transition-all border border-slate-700/50"
            data-testid="rate-btn"
          >
            <Star className="w-4 h-4" />
            Rate
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
